/**
 * Agent Card Component
 * Displays individual agent information with actions
 */

import {
  MoreVertical,
  Power,
  Settings,
  Trash2,
  CheckCircle,
  MessageSquare,
  Copy,
  Download,
} from 'lucide-react';
import { useState } from 'react';
import type { Agent } from '@/types/agent.types';
import { AgentChatHistory } from './AgentChatHistory';
import { AgentConfigurationEditor } from './AgentConfigurationEditor';
import { useAdmin } from '@/context/AdminContext';

interface AgentCardProps {
  agent: Agent;
  isSelected?: boolean;
  onSelect?: (agent: Agent) => void;
  onActivate?: (id: string) => void;
  onDeactivate?: (id: string) => void;
  onDelete?: (id: string) => void;
  onDuplicate?: (id: string, name?: string) => void;
  onExport?: (id: string) => void;
  onSetDefault?: (id: string) => void;
  onUpdateConfiguration?: (
    agentId: string,
    updates: {
      config?: Agent['config'];
      systemPrompt?: string;
      suggestedPrompts?: Agent['suggestedPrompts'];
      icon?: string;
    }
  ) => Promise<void>;
}

export function AgentCard({
  agent,
  isSelected = false,
  onSelect,
  onActivate,
  onDeactivate,
  onDelete,
  onDuplicate,
  onExport,
  onSetDefault,
  onUpdateConfiguration,
}: AgentCardProps) {
  const { isAdmin } = useAdmin();
  const [showMenu, setShowMenu] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showConfigEditor, setShowConfigEditor] = useState(false);

  const getStatusColor = (status: Agent['status']) => {
    switch (status) {
      case 'active':
        return 'bg-success-light text-success';
      case 'inactive':
        return 'bg-bg-secondary text-text-primary';
      case 'error':
        return 'bg-error-light text-error';
      case 'installing':
        return 'bg-warning-light text-warning';
      default:
        return 'bg-bg-secondary text-text-primary';
    }
  };

  const getFrameworkLabel = (framework: Agent['framework']) => {
    switch (framework) {
      case 'mcp':
        return 'MCP Server';
      case 'copilot':
        return 'Copilot';
      case 'azure-ai-agent':
        return 'Azure AI Agent';
      default:
        return framework;
    }
  };

  return (
    <div
      className={`
        relative rounded-lg border-2 p-4 transition-all hover:shadow-md
        cursor-pointer
        focus-visible:outline-none focus-visible:ring-2
        focus-visible:ring-stellar-primary focus-visible:ring-offset-2
        ${
          isSelected
            ? 'border-stellar-primary bg-bg-tertiary'
            : 'border-border-default bg-bg-primary'
        }
      `}
      onClick={() => onSelect?.(agent)}
      role="button"
      tabIndex={0}
      aria-label={`Agent: ${agent.name}`}
      onKeyDown={(e) => {
        // Don't handle keyboard events if they originated from an input or textarea
        const target = e.target as HTMLElement;
        const isInputElement =
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable;

        if (!isInputElement && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onSelect?.(agent);
        }
      }}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 text-2xl">
          {agent.icon || '🤖'}
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          {/* Header with default badge and menu */}
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <h3 className="truncate font-semibold text-text-primary">
                  {agent.name}
                </h3>
                {/* Default badge (inline) */}
                {agent.isDefault && (
                  <span
                    className={
                      'inline-flex items-center gap-1 rounded-full ' +
                      'bg-stellar-primary px-2 py-0.5 text-xs font-medium ' +
                      'text-white flex-shrink-0'
                    }
                  >
                    <CheckCircle className="h-3 w-3" />
                    Default
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-text-secondary line-clamp-2">
                {agent.description}
              </p>
            </div>

            {/* Menu */}
            <div className="relative flex-shrink-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(!showMenu);
                }}
                className={
                  'rounded p-1 hover:bg-bg-secondary ' +
                  'focus-visible:outline-none focus-visible:ring-2 ' +
                  'focus-visible:ring-stellar-primary'
                }
                aria-label="Agent actions menu"
                aria-haspopup="menu"
                aria-expanded={showMenu}
              >
                <MoreVertical className="h-5 w-5 text-text-tertiary" />
              </button>

              {showMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowMenu(false)}
                    aria-hidden="true"
                  />
                  <div
                    className="absolute right-0 top-8 z-20 w-48 rounded-lg border border-border-default bg-bg-primary py-1 shadow-lg"
                    role="menu"
                    aria-label="Agent actions"
                  >
                    {agent.status === 'inactive' && onActivate && (
                      <button
                        role="menuitem"
                        onClick={(e) => {
                          e.stopPropagation();
                          onActivate(agent.id);
                          setShowMenu(false);
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-bg-secondary"
                      >
                        <Power className="h-4 w-4" />
                        Activate
                      </button>
                    )}
                    {agent.status === 'active' && !agent.isDefault && onDeactivate && (
                      <button
                        role="menuitem"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeactivate(agent.id);
                          setShowMenu(false);
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-bg-secondary"
                      >
                        <Power className="h-4 w-4" />
                        Deactivate
                      </button>
                    )}
                    {!agent.isDefault && onSetDefault && (
                      <button
                        role="menuitem"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSetDefault(agent.id);
                          setShowMenu(false);
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-bg-secondary"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Set as Default
                      </button>
                    )}
                    <button
                      role="menuitem"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowHistory(true);
                        setShowMenu(false);
                      }}
                      className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-bg-secondary"
                    >
                      <MessageSquare className="h-4 w-4" />
                      View History
                    </button>
                    {isAdmin && onUpdateConfiguration && (
                      <button
                        role="menuitem"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowConfigEditor(true);
                          setShowMenu(false);
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-bg-secondary"
                      >
                        <Settings className="h-4 w-4" />
                        Edit Configuration
                      </button>
                    )}
                    {isAdmin && onExport && (
                      <button
                        role="menuitem"
                        onClick={(e) => {
                          e.stopPropagation();
                          onExport(agent.id);
                          setShowMenu(false);
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-bg-secondary"
                      >
                        <Download className="h-4 w-4" />
                        Export…
                      </button>
                    )}
                    {isAdmin && onDuplicate && (
                      <button
                        role="menuitem"
                        onClick={(e) => {
                          e.stopPropagation();
                          const suggested = `Copy of ${agent.name}`;
                          const input = prompt(
                            'Name for the duplicated agent:',
                            suggested
                          );
                          // null = user cancelled — do nothing.
                          // empty string after trim = let backend pick the
                          // default ("Copy of ...") rather than 400-erroring.
                          if (input === null) {
                            setShowMenu(false);
                            return;
                          }
                          const trimmed = input.trim();
                          onDuplicate(
                            agent.id,
                            trimmed.length > 0 ? trimmed : undefined
                          );
                          setShowMenu(false);
                        }}
                        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-bg-secondary"
                      >
                        <Copy className="h-4 w-4" />
                        Duplicate
                      </button>
                    )}
                    {!agent.isDefault && onDelete && (
                      <button
                        role="menuitem"
                        onClick={(e) => {
                          e.stopPropagation();
                          const message = `Delete agent "${agent.name}"? ` +
                            `This cannot be undone.`;
                          if (confirm(message)) {
                            onDelete(agent.id);
                          }
                          setShowMenu(false);
                        }}
                        className={
                          'flex w-full items-center gap-2 px-4 py-2 ' +
                          'text-left text-sm text-error hover:bg-error-light'
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span
              className={
                `rounded-full px-2 py-1 text-xs font-medium ` +
                `${getStatusColor(agent.status)}`
              }
            >
              {agent.status}
            </span>
            <span className="rounded-full bg-bg-tertiary px-2 py-1 text-xs font-medium text-text-primary">
              {getFrameworkLabel(agent.framework)}
            </span>
            {agent.capabilities.slice(0, 2).map((capability) => (
              <span
                key={capability}
                className="rounded-full bg-bg-secondary px-2 py-1 text-xs text-text-secondary"
              >
                {capability}
              </span>
            ))}
            {agent.capabilities.length > 2 && (
              <span className="text-xs text-text-tertiary">
                +{agent.capabilities.length - 2} more
              </span>
            )}
          </div>

          {/* Version */}
          {agent.metadata?.version && (
            <div className="mt-2 text-xs text-text-tertiary">
              v{agent.metadata.version}
            </div>
          )}
        </div>
      </div>

      {/* Chat History Modal */}
      {showHistory && (
        <AgentChatHistory agent={agent} onClose={() => setShowHistory(false)} />
      )}

      {/* Configuration Editor Modal - Admin Only */}
      {isAdmin && showConfigEditor && onUpdateConfiguration && (
        <AgentConfigurationEditor
          agent={agent}
          onSave={onUpdateConfiguration}
          onClose={() => setShowConfigEditor(false)}
        />
      )}
    </div>
  );
}
